import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { fetchGoldPrice, fetchExchangeRates } from '../services/api';
import { baseGoldPrices, currencyConfig, getRandomFluctuation } from '../data/mockData';

const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export const CurrencyProvider = ({ children }) => {
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [exchangeRates, setExchangeRates] = useState({ USD: 1, EUR: 0.92, TRY: 38.45, AED: 3.67 });
  const [goldPriceUSD, setGoldPriceUSD] = useState(4350.00); // Current market price ~$4,350/oz
  const [prices, setPrices] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [apiStatus, setApiStatus] = useState({ gold: false, currency: false });

  // Calculate prices based on selected currency
  const calculatePrices = useCallback((goldUSD, rates, currency) => {
    const rate = rates[currency] || 1;
    
    return baseGoldPrices.map(item => {
      let baseInUSD;
      
      // First calculate base price in USD
      if (item.alias === 'GOLD') {
        baseInUSD = goldUSD; // Gold ounce price directly from API
      } else if (item.alias === 'GRAM') {
        baseInUSD = goldUSD / 31.1035; // Troy ounce to gram
      } else if (item.alias === 'KILO') {
        baseInUSD = goldUSD * 32.1507; // Ounce to KG
      } else if (item.alias === 'ELIZ') {
        // Elizabeth Sovereign: 0.2354 oz pure gold + ~8% premium
        baseInUSD = goldUSD * 0.2354 * 1.08;
      } else {
        baseInUSD = item.basePrice; // Use base price for other metals
      }
      
      // Then convert to selected currency
      const baseInCurrency = baseInUSD * rate;

      const spreadInCurrency = item.spread * rate;
      const bid = getRandomFluctuation(baseInCurrency - spreadInCurrency / 2);
      const ask = getRandomFluctuation(baseInCurrency + spreadInCurrency / 2);

      return {
        ...item,
        bid: bid,
        ask: ask,
        prevBid: item.prevBid || bid,
        prevAsk: item.prevAsk || ask,
        currency: currency
      };
    });
  }, []);

  // Fetch data from APIs
  const fetchData = useCallback(async () => {
    setIsUpdating(true);
    
    try {
      const [goldResult, ratesResult] = await Promise.all([
        fetchGoldPrice(),
        fetchExchangeRates('USD')
      ]);

      setApiStatus({
        gold: goldResult.success,
        currency: ratesResult.success
      });

      if (goldResult.success) {
        setGoldPriceUSD(goldResult.price);
      }

      if (ratesResult.success) {
        // Add USD rate and ensure all needed currencies
        const rates = { USD: 1, ...ratesResult.rates };
        setExchangeRates(rates);
      }

      setLastUpdate(new Date());
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
      setIsUpdating(false);
    }
  }, []);

  // Update prices when currency or rates change
  useEffect(() => {
    const newPrices = calculatePrices(goldPriceUSD, exchangeRates, selectedCurrency);
    setPrices(prev => {
      return newPrices.map((item, index) => ({
        ...item,
        prevBid: prev[index]?.bid || item.bid,
        prevAsk: prev[index]?.ask || item.ask
      }));
    });
  }, [goldPriceUSD, exchangeRates, selectedCurrency, calculatePrices]);

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Periodic updates (every 60 seconds for real API, simulated fluctuations every 3 seconds)
  useEffect(() => {
    // Real API fetch every 60 seconds
    const apiInterval = setInterval(fetchData, 60000);
    
    // Simulated price fluctuations every 3 seconds (silent update - no "Updating" indicator)
    const fluctuationInterval = setInterval(() => {
      setPrices(prev => prev.map(item => ({
        ...item,
        prevBid: item.bid,
        prevAsk: item.ask,
        bid: getRandomFluctuation(item.bid, 0.02),
        ask: getRandomFluctuation(item.ask, 0.02)
      })));
      setLastUpdate(new Date());
    }, 3000);

    return () => {
      clearInterval(apiInterval);
      clearInterval(fluctuationInterval);
    };
  }, [fetchData]);

  const value = {
    selectedCurrency,
    setSelectedCurrency,
    exchangeRates,
    prices,
    lastUpdate,
    isLoading,
    isUpdating,
    apiStatus,
    currencyConfig,
    refreshData: fetchData,
    goldPriceUSD
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};
